import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link, type LinkProps } from 'react-router';
import css from './Botao.module.css';
import { atalhoParaAria, Kbd } from './Kbd';

export type VarianteBotao = 'primario' | 'secundario' | 'sucesso' | 'perigo' | 'fantasma';

interface OpcoesVisuais {
  variante?: VarianteBotao;
  pequeno?: boolean;
  largo?: boolean;
  /**
   * Tecla que faz o mesmo ("N", "Ctrl Enter"): aparece escrita no botão (some em tela de toque) e
   * vai em aria-keyshortcuts. Quem trata a tecla é a tela; o botão só mostra.
   */
  atalho?: string;
}

/** Classes de botão para um <a> externo (link com cara de botão, sem botão dentro de link). */
export function classesDeBotao(
  { variante = 'primario', pequeno, largo, atalho }: OpcoesVisuais,
  extra?: string,
) {
  return [
    css.botao,
    css[variante],
    pequeno && css.pequeno,
    largo && css.largo,
    comSeta({ variante, pequeno, largo }) && css.comSeta,
    principalComTecla({ variante, pequeno, largo, atalho }) && css.principal,
    extra,
  ]
    .filter(Boolean)
    .join(' ');
}

/** O principal largo (enviar formulário) leva a seta no círculo e o reflexo. */
function comSeta({ variante = 'primario', pequeno, largo }: OpcoesVisuais) {
  return variante === 'primario' && largo && !pequeno;
}

/** O principal com tecla ("Novo pedido N →", "Salvar pedido Ctrl Enter →"): 44 px, seta junto. */
function principalComTecla({ variante = 'primario', pequeno, largo, atalho }: OpcoesVisuais) {
  return variante === 'primario' && Boolean(atalho) && !largo && !pequeno;
}

function Seta({ solta }: { solta?: boolean }) {
  return (
    <span className={solta ? css.setaJunto : css.seta} aria-hidden="true">
      <svg
        aria-hidden="true"
        width={solta ? 16 : 20}
        height={solta ? 16 : 20}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M5 12h14M13 6l6 6-6 6" />
      </svg>
    </span>
  );
}

/** Miolo comum do botão e do link: texto, tecla escrita e seta. */
function Miolo({
  opcoes,
  carregando,
  children,
}: {
  opcoes: OpcoesVisuais;
  carregando?: boolean;
  children: ReactNode;
}) {
  const grande = comSeta(opcoes);
  const junto = principalComTecla(opcoes);
  return (
    <>
      {carregando && !grande && !junto && <span className={css.indicador} aria-hidden="true" />}
      {children}
      {opcoes.atalho && <Kbd aria-hidden="true">{opcoes.atalho}</Kbd>}
      {grande &&
        (carregando ? (
          <span className={css.seta} aria-hidden="true">
            <span className={css.indicador} />
          </span>
        ) : (
          <Seta />
        ))}
      {junto &&
        (carregando ? (
          <span className={css.setaJunto} aria-hidden="true">
            <span className={css.indicador} />
          </span>
        ) : (
          <Seta solta />
        ))}
    </>
  );
}

interface PropsBotao extends ButtonHTMLAttributes<HTMLButtonElement>, OpcoesVisuais {
  carregando?: boolean;
}

export function Botao({
  variante,
  pequeno,
  largo,
  atalho,
  carregando = false,
  disabled,
  className,
  children,
  type = 'button',
  ...resto
}: PropsBotao) {
  const opcoes = { variante, pequeno, largo, atalho };
  return (
    <button
      type={type}
      className={classesDeBotao(opcoes, className)}
      disabled={disabled || carregando}
      aria-busy={carregando || undefined}
      aria-keyshortcuts={atalho ? atalhoParaAria(atalho) : undefined}
      {...resto}
    >
      <Miolo opcoes={opcoes} carregando={carregando}>
        {children}
      </Miolo>
    </button>
  );
}

/** Link com cara de botão (navegação, não ação). */
export function BotaoLink({
  variante,
  pequeno,
  largo,
  atalho,
  className,
  children,
  ...resto
}: LinkProps & OpcoesVisuais) {
  const opcoes = { variante, pequeno, largo, atalho };
  return (
    <Link
      className={classesDeBotao(opcoes, className)}
      aria-keyshortcuts={atalho ? atalhoParaAria(atalho) : undefined}
      {...resto}
    >
      <Miolo opcoes={opcoes}>{children}</Miolo>
    </Link>
  );
}
